import React, { useEffect, useMemo, useState } from "react";
import { Plus, X, ArrowRight } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button, Spinner } from "../components/common/UIKit";
import SearchableDropdown from "../components/common/SearchableDropdown";
import { getSettings } from "../api/settings";
import { listStates, listCounties } from "../api/counties";
import { createBulkCheckout } from "../api/subscriptions";
import { useAuthStore } from "../store/authStore";
import { readPricing, formatPrice, getLeadTypePrice, calcLeadTypeTotal } from "../utils/pricing";
import { LEAD_TYPES_META } from "../constants/leadTypes";
import { errMsg } from "../lib/utils";

export function LeadTypeCard({ leadType, counties, countyOptions, onAddCounty, onRemoveCounty, onChangeCounty, pricing }) {
  const subtotal = calcLeadTypeTotal(leadType.name, counties.filter(Boolean).length || 0, pricing);
  const pricePerCounty = pricing.pricePerCounty;
  const usedIds = new Set(counties.filter(Boolean));

  return (
    <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden" data-testid={`lt-card-${leadType.name}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
        <span className="text-sm font-bold text-zinc-950">{leadType.label}</span>
        <span className="text-sm font-bold text-zinc-950">{formatPrice(subtotal)}<span className="text-xs font-normal text-zinc-400">/mo</span></span>
      </div>

      <div className="px-4 py-3 space-y-2">
        {counties.map((countyId, i) => {
          const isIncluded = i === 0;
          const available = countyOptions.filter((c) => !usedIds.has(c.value) || c.value === countyId);
          return (
            <div key={i} className="flex items-center gap-2" data-testid={`lt-${leadType.name}-county-row-${i}`}>
              <div className="flex-1">
                <SearchableDropdown
                  testId={`lt-${leadType.name}-county-${i}`}
                  value={countyId}
                  onChange={(v) => onChangeCounty(leadType.name, i, v)}
                  options={available}
                  placeholder="Select county"
                  searchPlaceholder="Search counties..."
                  clearable={false}
                />
              </div>
              {isIncluded ? (
                <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                  Included
                </span>
              ) : (
                <span className="flex-shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                  +{formatPrice(pricePerCounty)}/mo
                </span>
              )}
              {!isIncluded && (
                <button
                  type="button"
                  onClick={() => onRemoveCounty(leadType.name, i)}
                  className="flex-shrink-0 p-1 rounded text-zinc-400 hover:text-red-600 hover:bg-red-50"
                  data-testid={`lt-${leadType.name}-remove-county-${i}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        })}

        <button
          type="button"
          onClick={() => onAddCounty(leadType.name)}
          className="w-full flex items-center justify-center gap-1 text-xs font-semibold text-zinc-500 hover:text-zinc-950 border border-dashed border-zinc-200 hover:border-zinc-400 rounded-md py-2 transition-colors"
          data-testid={`lt-${leadType.name}-add-county`}
        >
          <Plus className="h-3 w-3" /> Add county
        </button>
      </div>
    </div>
  );
}

export default function PriceConfigurePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const initialCount = Math.max(1, parseInt(searchParams.get("count") || "1", 10) || 1);

  const initialLeadTypes = useMemo(() => {
    const raw = searchParams.get("lead_types") || "";
    if (!raw) return [];
    return raw.split(",").map((k) => k.trim()).filter((k) => LEAD_TYPES_META.some((l) => l.name === k));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [countyOptions, setCountyOptions] = useState([]);
  const [billing, setBilling] = useState(searchParams.get("billing") === "yearly" ? "yearly" : "monthly");
  const [selectedLeadTypes, setSelectedLeadTypes] = useState(initialLeadTypes);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState("");

  const [ltCounties, setLtCounties] = useState(() => {
    const init = {};
    initialLeadTypes.forEach((name) => { init[name] = Array.from({ length: initialCount }, () => ""); });
    return init;
  });

  useEffect(() => {
    getSettings().then(setSettings).catch(() => setSettings(null)).finally(() => setSettingsLoading(false));
    listStates().then(setStates).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedState) { setCountyOptions([]); return; }
    listCounties({ state_code: selectedState })
      .then((r) => setCountyOptions((r.items || []).map((c) => ({ value: c.id, label: c.name }))))
      .catch(() => setCountyOptions([]));
  }, [selectedState]);

  const pricing = useMemo(() => readPricing(settings || {}), [settings]);
  const { pricePerCounty, discountPercent } = pricing;

  const toggleLeadType = (name) => {
    setSelectedLeadTypes((prev) => {
      if (prev.includes(name)) {
        const next = prev.filter((k) => k !== name);
        setLtCounties((c) => { const n = { ...c }; delete n[name]; return n; });
        return next;
      }
      setLtCounties((c) => {
        const existingCounty = Object.values(c).map((slots) => slots?.[0]).find(Boolean) || "";
        const slots = Array.from({ length: initialCount }, (_, i) => (i === 0 ? existingCounty : ""));
        return { ...c, [name]: slots };
      });
      return [...prev, name];
    });
  };

  const handleStateChange = (v) => {
    setSelectedState(v);
    setLtCounties((prev) => {
      const next = {};
      Object.keys(prev).forEach((k) => { next[k] = prev[k].map(() => ""); });
      return next;
    });
  };

  const addCounty = (ltName) => {
    setLtCounties((prev) => ({ ...prev, [ltName]: [...(prev[ltName] || [""]), ""] }));
  };

  const removeCounty = (ltName, idx) => {
    setLtCounties((prev) => ({ ...prev, [ltName]: prev[ltName].filter((_, i) => i !== idx) }));
  };

  const changeCounty = (ltName, idx, val) => {
    setLtCounties((prev) => {
      const next = { ...prev };
      const arr = [...(next[ltName] || [])];
      arr[idx] = val;
      next[ltName] = arr;
      if (idx === 0) {
        Object.keys(next).forEach((name) => {
          if (name !== ltName && !next[name]?.[0]) {
            const other = [...(next[name] || [])];
            other[0] = val;
            next[name] = other;
          }
        });
      }
      return next;
    });
  };

  const ltSubtotals = useMemo(() => {
    const out = {};
    selectedLeadTypes.forEach((name) => {
      const filled = (ltCounties[name] || []).filter(Boolean).length;
      out[name] = calcLeadTypeTotal(name, filled, pricing);
    });
    return out;
  }, [selectedLeadTypes, ltCounties, pricing]);

  const monthlyTotal = useMemo(() => Object.values(ltSubtotals).reduce((a, b) => a + b, 0), [ltSubtotals]);

  const total = useMemo(() => {
    if (billing === "yearly") return Math.round(monthlyTotal * 12 * (1 - discountPercent / 100));
    return monthlyTotal;
  }, [monthlyTotal, billing, discountPercent]);

  const canSubscribe = selectedLeadTypes.length > 0 &&
    selectedLeadTypes.every((name) => (ltCounties[name] || []).some(Boolean));

  const checkoutPayload = useMemo(() => ({
    billing,
    lead_types: selectedLeadTypes.map((name) => ({
      name,
      counties: (ltCounties[name] || []).filter(Boolean),
    })),
    total,
  }), [billing, selectedLeadTypes, ltCounties, total]);

  const handleSubscribe = async () => {
    setError("");
    if (!token) {
      const params = new URLSearchParams();
      params.set("checkout_plan", JSON.stringify(checkoutPayload));
      navigate(`/signup?${params.toString()}`);
      return;
    }
    setCheckoutLoading(true);
    try {
      const res = await createBulkCheckout(checkoutPayload);
      window.location.href = res.session_url;
    } catch (e) {
      const message = errMsg(e, "Could not start checkout");
      setError(message);
      toast.error(message);
      setCheckoutLoading(false);
    }
  };

  if (settingsLoading || checkoutLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Spinner />
        {checkoutLoading && (
          <>
            <p className="text-sm font-semibold text-zinc-950">Setting up your subscription...</p>
            <p className="text-xs text-zinc-500">Redirecting to Stripe</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">

        <a className="btn" href="/">← Back to main</a>

        <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-zinc-950 mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Configure your plan
        </h1>
        <p className="text-sm text-zinc-500 mb-8">
          Extra counties {formatPrice(pricePerCounty)}/mo each · includes 1 county per lead type
        </p>

        <div className="flex items-center gap-2 mb-6">
          {["monthly", "yearly"].map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => setBilling(b)}
              className={`h-8 px-4 rounded-full text-xs font-bold border transition-colors ${billing === b ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-950"}`}
            >
              {b === "monthly" ? "Monthly" : `Yearly (save ${discountPercent}%)`}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-zinc-200 p-4 mb-4">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">State</p>
          <SearchableDropdown
            testId="configure-state"
            value={selectedState}
            onChange={handleStateChange}
            options={states.map((s) => ({ value: s.state_code, label: s.state }))}
            placeholder="Select a state"
            searchPlaceholder="Search states..."
          />
        </div>

        <div className="bg-white rounded-lg border border-zinc-200 p-4 mb-4">
          <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Lead Types</p>
          <div className="grid grid-cols-2 gap-2">
            {LEAD_TYPES_META.map((lt) => {
              const on = selectedLeadTypes.includes(lt.name);
              const ltPrice = getLeadTypePrice(lt.name, pricing);
              return (
                <label
                  key={lt.name}
                  className={`flex items-center justify-between gap-2 px-3 py-2 rounded-md border text-sm font-semibold cursor-pointer transition-colors ${on ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-950"}`}
                  data-testid={`configure-lt-${lt.name}`}
                >
                  <span className="flex items-center gap-2">
                    <input type="checkbox" checked={on} onChange={() => toggleLeadType(lt.name)} className="h-3.5 w-3.5 accent-zinc-950" />
                    {lt.label}
                  </span>
                  <span className={`text-xs font-bold ${on ? "text-zinc-300" : "text-zinc-400"}`}>{formatPrice(ltPrice)}/mo</span>
                </label>
              );
            })}
          </div>
        </div>

        {selectedLeadTypes.length > 0 && !selectedState && (
          <p className="text-xs text-amber-600 font-semibold mb-3">← Select a state to pick counties</p>
        )}

        {selectedLeadTypes.length > 0 && (
          <div className="space-y-3 mb-4">
            {selectedLeadTypes.map((name) => {
              const lt = LEAD_TYPES_META.find((l) => l.name === name);
              return (
                <LeadTypeCard
                  key={name}
                  leadType={lt}
                  counties={ltCounties[name] || [""]}
                  countyOptions={countyOptions}
                  onAddCounty={addCounty}
                  onRemoveCounty={removeCounty}
                  onChangeCounty={changeCounty}
                  pricing={pricing}
                />
              );
            })}
          </div>
        )}

        {selectedLeadTypes.length > 0 && (
          <div className="bg-white rounded-lg border border-zinc-200 p-5 mb-4" data-testid="configure-summary">
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Summary</p>
            <div className="space-y-1.5 mb-3">
              {selectedLeadTypes.map((name) => {
                const lt = LEAD_TYPES_META.find((l) => l.name === name);
                const count = (ltCounties[name] || []).filter(Boolean).length;
                return (
                  <div key={name} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-600">{lt?.label} <span className="text-zinc-400">({count} {count === 1 ? "county" : "counties"})</span></span>
                    <span className="font-semibold text-zinc-950">{formatPrice(ltSubtotals[name])}/mo</span>
                  </div>
                );
              })}
            </div>
            <div className="border-t border-zinc-100 pt-3 flex items-baseline justify-between">
              <span className="text-xs font-bold uppercase tracking-widest text-zinc-500">Total</span>
              <div className="text-right">
                <span className="text-3xl font-black tracking-tight text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  {formatPrice(total)}
                </span>
                <span className="text-sm text-zinc-400">{billing === "yearly" ? "/yr" : "/mo"}</span>
              </div>
            </div>
            {billing === "yearly" && (
              <p className="text-xs text-green-600 font-semibold text-right mt-1">Save {discountPercent}% — billed yearly</p>
            )}
          </div>
        )}

        <Button
          className="w-full h-11"
          disabled={!canSubscribe || checkoutLoading}
          onClick={handleSubscribe}
          data-testid="configure-subscribe"
        >
          Subscribe <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
        {error && <p className="text-xs text-red-600 font-semibold mt-3">{error}</p>}

      </div>
    </div>
  );
}
